import React, { useState ,useEffect} from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  useTheme,
  Divider,
} from "@mui/material";

import LockIcon from "@mui/icons-material/Lock";
import ReplayIcon from "@mui/icons-material/Replay";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowBackIosNewOutlined from "@mui/icons-material/ArrowBackIosNewOutlined";

import { Outlet, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import axios from "axios";
import { baseapi } from "../../Environmentalurl";
import { ApplicationType, entity_id, getGeolocation, location, Platform } from "../../Utils/Const";
import useIPAddress from "../../Utils/useIPAddress";
import DashboardHeader from "../../Dashboard/DashboardHeader";
import api from "../../Utils/Api";

const TpinLayout = () => {
  const [isEnabled, setIsEnabled] = useState(true);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [enteredOtp, setEnteredOtp] = useState("");
  const [nextPath, setNextPath] = useState(null);
  const [mobileLast4, setMobileLast4] = useState("");

  const navigate = useNavigate();
  const ip = useIPAddress();

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const handlehomenavigation = () => {
    navigate("/profile-settings");
  };

  const getMobileFromSession = () => {
    const raw = sessionStorage.getItem("mobileNumber");
    if (!raw) return null;
    try {
      return String(JSON.parse(raw));
    } catch {
      return raw;
    }
  };

  const options = [
    {
      icon: <LockIcon color="success" />,
      title: "Change TPIN",
      subtitle: "Update your existing TPIN",
      path: "/profile-settings/changetpin",
    },
    {
      icon: <ReplayIcon color="success" />,
      title: "Forgot TPIN - Reset via OTP",
      subtitle: "Reset using OTP sent to your registered mobile",
      path: "/profile-settings/updatetpin",
    },
  ];

  const handleOptionClick = (option) => {
    if (option.title === "Forgot TPIN - Reset via OTP") {
      generateOtp(option.path);
    } else {
      navigate(option.path);
    }
  };

  const generateOtp = async (path) => {
    const mobile = getMobileFromSession();
    if (!mobile) {
      toast.error("Mobile number not found in session. Please login again.");
      return;
    }

    setMobileLast4(mobile.slice(-4));

    const requestBody = {
      ReqType: "MT_Reg_GenerateOTP",
      Mobilenumber: mobile,
      Purpose: "GenerateOTP",
      EntityID: entity_id,
      TrxnAmount: "0",
      Platform: Platform,
      ApplicationType: ApplicationType,
      GeoLocation: location,
      MachineID: ip,
    };

    try {
      const response = await fetch(`${baseapi}/auth/generateotp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (data.Response_Code === "1000") {
        toast.success("OTP sent successfully!");
        sessionStorage.setItem("tpinid", data.MessageRefID);
        setOtpDialogOpen(true);
        setNextPath(path);
      } else {
        toast.error("Failed to generate OTP: " + data.Message);
      }
    } catch (error) {
      toast.error("Something went wrong while generating OTP.");
    }
  };

  const handleOtpSubmit = async () => {
    if (!enteredOtp) {
      toast.error("Please enter the OTP.");
      return;
    }

    const messageRefId = sessionStorage.getItem("tpinid");
    const mobile = getMobileFromSession();

    if (!mobile || !messageRefId) {
      toast.error("Missing session data. Please request OTP again.");
      setOtpDialogOpen(false);
      return;
    }

    const requestBody = {
      ReqType: "MT_Reg_ValidateOTP",
      Mobilenumber: mobile,
      MessageRefID: messageRefId,
      OTP: enteredOtp,
      Platform: Platform,
      EntityID: entity_id,
      GeoLocation: location,
      MachineID: ip,
      DeviceID: "fd2d6da32e083a77",
      VersionName: "1.5",
      VersionCode: 7,
    };

    try {
      const response = await api.post(`${baseapi}/auth/validateotp`, requestBody);
      const data = response.data;

      if (data.Response_Code === "1000") {
        toast.success("OTP validated!");
        setOtpDialogOpen(false);
        setEnteredOtp("");
        navigate(nextPath || "/profile-settings/updatetpin");
      } else {
        toast.error("OTP validation failed: " + data.Message);
      }
    } catch (error) {
      toast.error("Error validating OTP.");
    }
  };

  return (
    <>
     <DashboardHeader/> 
      {/* PAGE WRAPPER */}
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          backgroundColor: isDark ? "#0a122a" : "#f4f7fb",
          p: { xs: 2, md: 4 },
        }}
      >
        {/* HEADER SECTION */}

        {/* TPIN CARD SECTION */}
        <Card
          sx={{
            width: "100%",
            maxWidth: 550,
            mx: "auto",
            mt: { xs: 4, sm: 5, md: 10 },
            borderRadius: 3,
            boxShadow: isDark
              ? "0px 0px 10px rgba(255,255,255,0.06)"
              : "0px 4px 12px rgba(0,0,0,0.1)",
            backgroundColor: isDark ? "#1e293b" : "#ffffff",
            transition: "0.3s",
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box display="flex" alignItems="center">
              <IconButton onClick={() => navigate("/profile-settings")}>
                <ArrowBackIosNewIcon
                  fontSize="small"
                  sx={{ color: isDark ? "#e2e8f0" : "#111" }}
                />
              </IconButton>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ color: isDark ? "#f1f5f9" : "#111" }}
              >
                <LockIcon fontSize="small" color="success" sx={{ mr: 1 }} />
                TPIN Options
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography
                mt={1}
                variant="body2"
                sx={{ color: isDark ? "#cbd5e1" : "text.secondary" }}
              >
                Manage your Transaction PIN securely
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={isEnabled}
                    onChange={(e) => setIsEnabled(e.target.checked)}
                  />
                }
                label={isEnabled ? "Enable TPIN" : "Disable TPIN"}
                sx={{ color: isDark ? "#e2e8f0" : "#111" }}
              />
            </Box>

            <List sx={{ mt: 2 }}>
              {options.map((option, index) => (
                <Card
                  key={index}
                  sx={{
                    mb: 2,
                    backgroundColor: isDark ? "#243044" : "#f9fafa",
                    boxShadow: "none",
                    borderRadius: 2,
                    transition: "0.3s",
                    "&:hover": {
                      cursor: "pointer",
                      backgroundColor: isDark ? "#2f3a4d" : "#f1f5f4",
                    },
                  }}
                  onClick={() => handleOptionClick(option)}
                >
                  <CardContent sx={{ py: 1.5, px: 2 }}>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {option.icon}
                      </ListItemIcon>

                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: isDark ? "#e2e8f0" : "#111",
                            }}
                          >
                            {option.title}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            sx={{ color: isDark ? "#94a3b8" : "#555" }}
                          >
                            {option.subtitle}
                          </Typography>
                        }
                      />

                      <Typography
                        variant="body2"
                        sx={{
                          ml: "auto",
                          color: isDark ? "#cbd5e1" : "#444",
                        }}
                      >
                        &#8250;
                      </Typography>
                    </ListItem>
                  </CardContent>
                </Card>
              ))}
            </List>
          </Box>
        </Card>
      </Box>

      {/* OTP DIALOG */}
      <Dialog
        open={otpDialogOpen}
        onClose={() => setOtpDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? "#1e293b" : "#ffffff",
            color: isDark ? "#e2e8f0" : "#111",
          },
        }}
      >
        <DialogTitle sx={{ color: isDark ? "#f1f5f9" : "#111" }}>
          Enter OTP
        </DialogTitle>

        <DialogContent>
          <Typography
            variant="body2"
            sx={{ mb: 1, color: isDark ? "#cbd5e1" : "#333" }}
          >
            {mobileLast4
              ? `Please enter the OTP sent to your mobile ending with ${mobileLast4}.`
              : "Please enter the OTP sent to your registered mobile number."}
          </Typography>

          <TextField
            label="OTP"
            fullWidth
            margin="normal"
            value={enteredOtp}
            onChange={(e) => setEnteredOtp(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: isDark ? "#0f172a" : "#f7f7f7",
                color: isDark ? "#e2e8f0" : "#000",
              },
              "& .MuiInputLabel-root": {
                color: isDark ? "#cbd5e1" : "#555",
              },
            }}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOtpDialogOpen(false)}
            sx={{ color: isDark ? "#f1f5f9" : "#111" }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleOtpSubmit}
            sx={{
              backgroundColor: isDark ? "#10b981" : "#16a34a",
              "&:hover": {
                backgroundColor: isDark ? "#0d9d6b" : "#15803d",
              },
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="top-right" autoClose={4000} hideProgressBar />
    </>
  );
};

export default TpinLayout;
